import { useCallback, useEffect, useState } from "react";
import type { CoverageAreaInput, CoverageLookupHit, CoveragePartyInput } from "@/lib/coverage/coverageLookup";
import {
  fetchCoverageBoard,
  lookupCoverageAddress,
  milesToKm,
  saveCoverageAreas,
  saveCoverageParty,
  type CoverageLocationPin,
} from "@/lib/coverage/coverageClient";
import { draftFromParty, emptyPartyDraft, type PartyDraft } from "@/components/coverage/CoveragePartyEditor";

export function useCoverageBoard() {
  const [parties, setParties] = useState<CoveragePartyInput[]>([]);
  const [areas, setAreas] = useState<CoverageAreaInput[]>([]);
  const [locations, setLocations] = useState<CoverageLocationPin[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<PartyDraft>(emptyPartyDraft);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [address, setAddress] = useState("");
  const [program, setProgram] = useState("tube_steel");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [hits, setHits] = useState<CoverageLookupHit[]>([]);
  const [point, setPoint] = useState<{ lat: number; lng: number } | null>(null);
  const [precision, setPrecision] = useState<"rooftop" | "city" | null>(null);

  const reload = useCallback(async () => {
    const res = await fetchCoverageBoard();
    if (!res.ok) {
      setLoadError(res.error);
      return;
    }
    setLoadError(null);
    setParties(res.data.parties);
    setAreas(res.data.areas);
    setLocations(res.data.locations);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  function selectParty(id: string | null) {
    setSaveError(null);
    setDrawing(false);
    if (!id) {
      setDraft(emptyPartyDraft());
      return;
    }
    const party = parties.find((row) => row.id === id);
    if (!party) return;
    setDraft(draftFromParty(party, areas));
  }

  function acceptPolygon(geojson: unknown) {
    setDrawing(false);
    setDraft((current) => ({ ...current, polygonText: JSON.stringify(geojson) }));
  }

  async function save() {
    setSaving(true);
    setSaveError(null);
    const radii = parseRadii(draft.preferredMiles, draft.fullMiles);
    if (!radii.ok) {
      setSaving(false);
      setSaveError(radii.error);
      return;
    }
    let polygon: unknown = null;
    if (draft.polygonText.trim()) {
      try {
        polygon = JSON.parse(draft.polygonText) as unknown;
      } catch {
        setSaving(false);
        setSaveError("Polygon GeoJSON is not valid JSON.");
        return;
      }
    }
    const partyRes = await saveCoverageParty({
      id: draft.id,
      name: draft.name,
      program: draft.program,
      color: draft.color,
      pricing_url: draft.pricingUrl,
      address: draft.address,
      active: true,
    });
    if (!partyRes.ok) {
      setSaving(false);
      setSaveError(partyRes.error);
      return;
    }
    const partyId = partyRes.data.party.id;
    setDraft((current) => ({ ...current, id: partyId }));
    const areaRes = await saveCoverageAreas({
      party_id: partyId,
      preferred_radius_km: radii.preferred,
      full_radius_km: radii.full,
      polygon,
    });
    setSaving(false);
    if (!areaRes.ok) {
      setSaveError(areaRes.error);
      return;
    }
    setDraft((current) => ({ ...current, id: partyId, address: "" }));
    await reload();
  }

  async function lookup() {
    setLookupBusy(true);
    setLookupError(null);
    const res = await lookupCoverageAddress({ address, program });
    setLookupBusy(false);
    if (!res.ok) {
      setHits([]);
      setPoint(null);
      setPrecision(null);
      setLookupError(res.error);
      return;
    }
    setHits(res.data.hits);
    setPoint({ lat: res.data.point.lat, lng: res.data.point.lng });
    setPrecision(res.data.point.precision);
  }

  const draftPolygon = parseDraftPolygon(draft.polygonText);

  return {
    parties,
    areas,
    locations,
    loadError,
    draft,
    setDraft,
    selectParty,
    save,
    saving,
    saveError,
    drawing,
    setDrawing,
    acceptPolygon,
    address,
    setAddress,
    program,
    setProgram,
    lookup,
    lookupBusy,
    lookupError,
    hits,
    point,
    precision,
    draftPolygon,
  };
}

function parseRadii(preferredMiles: string, fullMiles: string): { ok: true; preferred: number | null; full: number | null } | { ok: false; error: string } {
  const preferred = milesOrNull(preferredMiles);
  const full = milesOrNull(fullMiles);
  if (preferred === "bad" || full === "bad") return { ok: false, error: "Radius must be a number of miles." };
  if (preferred != null && full != null && preferred > full) {
    return { ok: false, error: "Preferred radius is larger than the full territory." };
  }
  return { ok: true, preferred, full };
}

function milesOrNull(text: string): number | null | "bad" {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const miles = Number(trimmed);
  if (!Number.isFinite(miles) || miles <= 0) return "bad";
  return milesToKm(miles);
}

function parseDraftPolygon(text: string): unknown | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return null;
  }
}
